import api from './apiClient';

export type OnboardingTaskKey = 
  | 'record_first_video'
  | 'create_scenes_project'
  | 'try_layouts'
  | 'export_share';

export interface OnboardingTask {
  key: OnboardingTaskKey;
  label: string;
  order: number;
  completed: boolean;
  completedAt: string | null;
}

export interface OnboardingProgress {
  tasks: OnboardingTask[];
  completedCount: number;
  totalCount: number;
  allCompleted: boolean;
  progress: number;
}

class OnboardingService {
  private listeners: Set<() => void> = new Set();

  async getProgress(): Promise<OnboardingProgress | null> {
    try {
      const response = await api.get('/api/onboarding');
      return response.data;
    } catch (error) {
      console.error('[Onboarding] Failed to fetch progress:', error);
      return null;
    }
  }

  async completeTask(taskKey: OnboardingTaskKey): Promise<boolean> {
    try {
      await api.post(`/api/onboarding/${taskKey}/complete`);
      console.log(`[Onboarding] Task completed: ${taskKey}`);
      this.notifyListeners();
      return true;
    } catch (error) {
      console.error(`[Onboarding] Failed to complete task ${taskKey}:`, error);
      return false;
    }
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  async markRecordFirstVideo(): Promise<void> {
    await this.completeTask('record_first_video');
  }

  async markCreateScenesProject(): Promise<void> {
    await this.completeTask('create_scenes_project');
  }

  async markTryLayouts(): Promise<void> {
    await this.completeTask('try_layouts');
  }

  async markExportShare(): Promise<void> {
    await this.completeTask('export_share');
  }
}

export const onboardingService = new OnboardingService();
export default onboardingService;
